import {useRef, useEffect} from 'react';
import {__} from '@wordpress/i18n';
import {registerPaymentMethod} from '@woocommerce/blocks-registry';
import {type CheckoutAfterProcessingWithErrorEventData} from '@woocommerce/checkout';
import {getSetting} from '@woocommerce/settings';
import {loadStripe, createSetupIntent} from '../stripe';
import {type PaymentMethodInterface} from '@woocommerce/types';
import {startTransaction, updateTransaction, z} from '@peachpay/checkout-blocks';
import CurrencyFallback from '../../../../../blocks/src/currency-fallback';
import {parsePaymentData} from '../stripe';
import {getErrorMessage} from '../../../../../../frontend/@shared/ts/error';
import {decodeEntities} from '@wordpress/html-entities';

const CardSettingsSchema = z.object({
	icon: z.string(),
	title: z.string(),
	description: z.string(),
	supports: z.string().array(),
	connect_id: z.string(),
	public_key: z.string(),
	setup_intent_url: z.string(),
	setup_intent_nonce: z.string(),
	currency_fallback_required: z.union([z.string(), z.null()]),
});

const peachpayStripeCardData = CardSettingsSchema.safeParse(
	getSetting('peachpay_stripe_card_data', false),
);

if (!peachpayStripeCardData.success) {
	console.error(peachpayStripeCardData.error);
	throw new Error(__('PeachPay Stripe Card data is not available', 'peachpay-for-woocommerce'));
}

const method_data = peachpayStripeCardData.data;
const title = decodeEntities(method_data.title);
const description = decodeEntities(method_data.description);

loadStripe(method_data.public_key, method_data.connect_id)
	.then(stripe => {
		if (stripe === null) {
			throw new Error(__('Stripe is null', 'peachpay-for-woocommerce'));
		}

		if (typeof stripe === 'string') {
			throw new Error(stripe);
		}

		const style = {
			base: {
				color: '#333',
				fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
				fontSmoothing: 'antialiased',
				fontSize: '16px',
				'::placeholder': {
					color: '#6a6a6a',
				},
			},
		};

		const elements = stripe.elements();
		const cardElement = elements.create('card', {style, hidePostalCode: true});

		/**
     * Content component
     */
		const Content = (context: PaymentMethodInterface) => {
			const {emitResponse, eventRegistration, billing} = context;
			const {onPaymentSetup, onCheckoutSuccess, onCheckoutFail} = eventRegistration;

			const containerRef = useRef<HTMLDivElement | null>(null);

			useEffect(() => {
				if (containerRef.current) {
					containerRef.current.innerHTML = '';
					cardElement.mount(containerRef.current);
				}
			}, []);

			useEffect(() => {
				const unsubscribe = onPaymentSetup(async () => {
					try {
						const start_transaction = await startTransaction('peachpay_stripe_card');

						if (start_transaction.error) {
							return {
								type: emitResponse.responseTypes.ERROR,
								message: getErrorMessage(start_transaction.error),
							};
						}

						if (start_transaction.result === undefined) {
							return {
								type: emitResponse.responseTypes.ERROR,
								message: __('Missing transaction ID', 'peachpay-for-woocommerce'),
							};
						}

						const transaction_ID = start_transaction.result;

						const tokenResult = await stripe.createPaymentMethod({
							type: 'card',
							card: cardElement,
							billing_details: {
								address: {
									city: billing.billingAddress.city,
									country: billing.billingAddress.country,
									line1: billing.billingAddress.address_1,
									line2: billing.billingAddress.address_2,
									postal_code: billing.billingAddress.postcode,
									state: billing.billingAddress.state,
								},
								email: billing.billingAddress.email,
								name: billing.billingAddress.first_name + ' ' + billing.billingAddress.last_name,
								phone: billing.billingAddress.phone,
							},
						});

						if (tokenResult.error) {
							const note = tokenResult.error.message ?? __('Failed to tokenize payment method.', 'peachpay-for-woocommerce');
							await updateTransaction(transaction_ID, {paymentStatus: 'creationfailed', note});
							return {
								type: emitResponse.responseTypes.ERROR,
								message: note,
							};
						}

						// Return here because setup intents are only needed to handle 0 dollar payments
						if (billing.cartTotal.value > 0) {
							return {
								type: emitResponse.responseTypes.SUCCESS,
								meta: {
									paymentMethodData: {
										peachpay_transaction_id: transaction_ID,
										peachpay_stripe_payment_method_id: tokenResult.paymentMethod.id,
									},
								},
							};
						}

						const setupIntentResult = await createSetupIntent({
							payment_method_type: 'card',
							payment_method_ID: tokenResult.paymentMethod.id,
							setup_intent_url: method_data.setup_intent_url,
							setup_intent_nonce: method_data.setup_intent_nonce,
						});

						if (setupIntentResult === null) {
							const note = __('Failed to create setup intent.', 'peachpay-for-woocommerce');
							await updateTransaction(transaction_ID, {paymentStatus: 'creationfailed', note});
							return {
								type: emitResponse.responseTypes.ERROR,
								message: note,
							};
						}

						const {error: confirmError, setupIntent: confirmIntent} = await stripe.confirmCardSetup(setupIntentResult.setup_intent_details.client_secret);
						if (confirmError) {
							const note = confirmError.message ?? __('Failed to confirm setup intent.', 'peachpay-for-woocommerce');
							await updateTransaction(transaction_ID, {paymentStatus: 'creationfailed', note});
							return {
								type: emitResponse.responseTypes.ERROR,
								message: note,
							};
						}

						if (confirmIntent.status !== 'succeeded') {
							const note = __('Setup intent resulted in invalid status', 'peachpay-for-woocommerce') + `(${confirmIntent.status})`;
							await updateTransaction(transaction_ID, {paymentStatus: 'creationfailed', note});
							return {
								type: emitResponse.responseTypes.ERROR,
								message: note,
							};
						}

						if (typeof confirmIntent.payment_method === 'object' || confirmIntent.payment_method === null) {
							const note = __('Payment method invalid type:', 'peachpay-for-woocommerce') + `(${typeof confirmIntent.payment_method})`;
							await updateTransaction(transaction_ID, {paymentStatus: 'creationfailed', note});
							return {
								type: emitResponse.responseTypes.ERROR,
								message: note,
							};
						}

						return {
							type: emitResponse.responseTypes.SUCCESS,
							meta: {
								paymentMethodData: {
									peachpay_transaction_id: transaction_ID,
									peachpay_stripe_payment_method_id: confirmIntent.payment_method,
								},
							},
						};
					} catch (error) {
						return {
							type: emitResponse.responseTypes.ERROR,
							message: (error as Error).message,
						};
					}
				});

				return unsubscribe;
			}, [
				billing.billingAddress,
				billing.cartTotal.value,
				onPaymentSetup,
			]);

			useEffect(() => {
				const unsubscribe = onCheckoutSuccess(async (data: {redirectUrl: string}) => {
					const dataURL = new URL(data.redirectUrl);

					const [name, encodedStringData] = dataURL.hash.split('=');

					if (dataURL.hash === '' || name !== '#payment_data' || !encodedStringData) {
						return;
					}

					const paymentData = parsePaymentData(encodedStringData);

					if (paymentData === null) {
						return {
							type: emitResponse.responseTypes.ERROR,
							message: __('Failed to parse payment data', 'peachpay-for-woocommerce'),
						};
					}

					const clientSecret = paymentData.data.client_secret;
					const successURL = paymentData.success_url;

					const {paymentIntent, error} = await stripe.retrievePaymentIntent(clientSecret);
					if (error) {
						return {
							type: emitResponse.responseTypes.ERROR,
							message: error?.message ?? __('Failed to retrieve payment intent.', 'peachpay-for-woocommerce'),
							messageContext: emitResponse.noticeContexts.PAYMENTS,
						};
					}

					if (paymentIntent.status === 'succeeded' || paymentIntent.status === 'requires_capture') {
						return {
							type: emitResponse.responseTypes.SUCCESS,
							redirectUrl: successURL,
						};
					}

					if (paymentIntent.status === 'requires_action') {
						const {paymentIntent, error} = await stripe.confirmCardPayment(clientSecret);

						if (error) {
							return {
								type: emitResponse.responseTypes.ERROR,
								message: error?.message ?? __('Failed to confirm payment intent.', 'peachpay-for-woocommerce'),
								messageContext: emitResponse.noticeContexts.PAYMENTS,
							};
						}

						if (
							paymentIntent.status === 'succeeded'
              || paymentIntent.status === 'requires_capture'
						) {
							return {
								type: emitResponse.responseTypes.SUCCESS,
								redirectUrl: successURL,
							};
						}

						return {
							type: emitResponse.responseTypes.ERROR,
							message: __('Confirmed payment intent invalid status:', 'peachpay-for-woocommerce') + `${paymentIntent.status}`,
							messageContext: emitResponse.noticeContexts.PAYMENTS,
						};
					}

					return {
						type: emitResponse.responseTypes.ERROR,
						message: __('Failed to confirm payment intent with status:', 'peachpay-for-woocommerce') + `${paymentIntent.status}`,
						messageContext: emitResponse.noticeContexts.PAYMENTS,
					};
				});

				return unsubscribe;
			}, [onCheckoutSuccess]);

			useEffect(() => {
				const unsubscribe = onCheckoutFail((data: CheckoutAfterProcessingWithErrorEventData) => {
					if (data.processingResponse?.paymentStatus === emitResponse.responseTypes.FAIL) {
						return {
							type: emitResponse.responseTypes.FAIL,
							message: data.processingResponse.paymentDetails['message'],
							messageContext: emitResponse.noticeContexts.PAYMENTS,
						};
					}

					return {
						type: emitResponse.responseTypes.FAIL,
						message:
              data.processingResponse?.message
              ?? __('Checkout failed unexpectedly', 'peachpay-for-woocommerce') + `${JSON.stringify(data)}`,
						messageContext: emitResponse.noticeContexts.PAYMENTS,
					};
				});

				return unsubscribe;
			}, [onCheckoutFail]);

			if (method_data.currency_fallback_required) {
				return (
					<div>
						<CurrencyFallback
							name={title}
							currency={method_data.currency_fallback_required}
						/>
					</div>
				);
			}

			return (
				<div>
					<div style={{marginBottom: '0.5rem'}}>{description}</div>
					<div ref={containerRef}>Loading...</div>
				</div>
			);
		};

		const SavedMethod = (context: PaymentMethodInterface & {token: string}) => {
			const {emitResponse, eventRegistration} = context;
			const {onPaymentSetup} = eventRegistration;

			useEffect(() => {
				const unsubscribe = onPaymentSetup(async () => {
					try {
						const start_transaction = await startTransaction('peachpay_stripe_card');

						if (start_transaction.error || !start_transaction.result) {
							throw start_transaction.error;
						}

						return {
							type: emitResponse.responseTypes.SUCCESS,
							meta: {
								paymentMethodData: {
									peachpay_transaction_id: start_transaction.result,
									'wc-peachpay_stripe_card-payment-token': context.token,
								},
							},
						};
					} catch (error) {
						return {
							type: emitResponse.responseTypes.ERROR,
							message: (error as Error).message,
						};
					}
				});

				return unsubscribe;
			}, [context, onPaymentSetup]);
		};

		/**
     * Label component
     *
     * @param {PaymentMethodInterface} props Props from payment API.
     */
		const Label = (props: PaymentMethodInterface) => {
			const {PaymentMethodLabel, PaymentMethodIcons} = props.components;
			return (
				<>
					<PaymentMethodLabel text={title} />
					<PaymentMethodIcons className='pp-label-icon' icons={[{
						id: 'peachpay_stripe_card',
						src: method_data.icon,
						alt: method_data.title,
					}]} />
				</>
			);
		};

		registerPaymentMethod({
			ariaLabel: title,
			canMakePayment(data) {
				return data.paymentMethods.includes('peachpay_stripe_card');
			},
			// @ts-expect-error Missing types are expected because WooCommerce gives the props
			content: <Content />,
			// @ts-expect-error Missing types are expected because WooCommerce gives the props
			edit: <Content />,
			// @ts-expect-error Missing types are expected because WooCommerce gives the props
			label: <Label />,
			// @ts-expect-error Missing types are expected because WooCommerce gives the props
			savedTokenComponent: <SavedMethod />,
			name: 'peachpay_stripe_card',
			supports: {
				features: method_data.supports,
				showSavedCards: !method_data.currency_fallback_required,
				showSaveOption: !method_data.currency_fallback_required,
			},
		});
	})
	.catch(error => {
		throw error as Error;
	});
